import { ExpandableCard } from "collections/Cards";
import { ToolTip, PolyIcon, ToggleSwitchInput, Loader } from "elements";
import { infiniteNestLink, useQueryParamString } from "polyrouter";
import React from "react";
import { ProtectedLink } from "TsRoleGuards";
import { LocalKeys, Trans, useTranslation } from "utilities/i18n";
import {
  ProvisionedSetting,
  SectionSetting
} from "./ProvisionedDeviceSettings";
import {
  Setting,
  useProvisionedDevicePolicyScopeContext
} from "./ProvisionedDeviceSettingsRouter";
import {
  UpdateNowButton,
  ProvisionedUpdateSoftwareForm
} from "./ProvisionedUpdateSoftware";
import {
  CategorySettings,
  ConfigParam,
  DEFAULT_GROUP,
  MySettingsProps,
  RevertAction,
  Settings,
  SubcategorySettings,
  useDiscardSetting,
  useRevertSetting
} from "./MySettings";
import { Animation } from "elements";
import darkAnimation from "assets/animations/Lens-Empty-Dark.json";
import lightAnimation from "assets/animations/Lens-Empty-Light.json";
import {
  DEVICE_PARAM_TYPE,
  MAIN,
  MY_SETTINGS_CHANGES_VIEW,
  MY_SETTINGS_DEVICE_CONFIG_VIEW,
  MY_SETTINGS_POLICY_VIEW
} from "Constants";
import { handleNullFields } from "utilities/stringUtils";
import { PolicyDeploymentScope } from "generated/graphql";

type SettingsMapperProps = Pick<
  MySettingsProps,
  "device" | "deviceModel" | "dirty"
> & {
  view: string;
  inheritedBy: string;
  settingsToDisplay: Settings<Setting | ConfigParam>;
  swUpdateSettingsToDisplay: Record<string, SectionSetting>;
  loading: boolean;
};

interface SettingProps {
  setting: Setting;
  category: string;
  subcategory: string;
  collection: string;
}

interface ConfigParamProps {
  configParam: ConfigParam;
}

const isSetting = (setting: Setting | ConfigParam): setting is Setting =>
  "settingName" in setting;

const PolicySetting: React.FC<SettingProps> = ({
  setting,
  category,
  subcategory,
  collection
}) => {
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const revertSetting = useRevertSetting();
  const hasRevert = setting.policyDeploymentScope === policyScope;
  const onRevert = hasRevert
    ? () =>
        revertSetting(
          category,
          subcategory,
          collection,
          setting.settingName,
          setting.type ?? ""
        )
    : undefined;

  return (
    <ProvisionedSetting
      setting={{ ...setting, category, subcategory, collection, show: true }}
      revert={{
        action: onRevert
      }}
    />
  );
};

const ChangedSetting: React.FC<SettingProps> = ({
  setting,
  category,
  subcategory,
  collection
}) => {
  const resetSettingChange = useDiscardSetting();

  return (
    <ProvisionedSetting
      setting={{ ...setting, category, subcategory, collection, show: true }}
      revert={{
        action: () =>
          resetSettingChange(
            category,
            subcategory,
            collection,
            setting.settingName
          ),
        icon: "clear_circle_outline",
        iconClassName: "outline"
      }}
    />
  );
};

const DeviceConfigParam: React.FC<ConfigParamProps> = ({ configParam }) => {
  const { t } = useTranslation();
  const { name, friendlyName, value, exportConfigType, type } = configParam;

  const source = exportConfigType
    ? t(`DEVICE_CONFIG_SOURCE_${exportConfigType}` as LocalKeys)
    : t("UNKNOWN");

  return (
    <div className="form-group" data-testid="form-setting">
      <label className="col-4 text-right">{friendlyName || name}:</label>
      <div className="col-3 overflow-hidden" data-testid={name + "-setting"}>
        {type === DEVICE_PARAM_TYPE.BOOL || type === DEVICE_PARAM_TYPE.XBOOL ? (
          <ToggleSwitchInput
            name={name ?? ""}
            checked={value === "True" || value === "1"}
            disabled={true}
            onChange={() => {}}
          />
        ) : (
          <span className="text-break">{handleNullFields(value)}</span>
        )}
      </div>
      <div className="col-3 text-md v-center" data-testid="source">
        {source}
      </div>
    </div>
  );
};

const Subcategory: React.FC<{
  category: string;
  subcategory: string;
  settings: SubcategorySettings<Setting | ConfigParam>;
}> = ({ category, subcategory, settings }) => {
  const [view] = useQueryParamString<"view">("view");

  const getSettingComponent = (
    setting: Setting | ConfigParam,
    collection: string
  ) => {
    return isSetting(setting) ? (
      <div key={setting.settingName}>
        {view === MY_SETTINGS_CHANGES_VIEW ? (
          <ChangedSetting
            setting={setting}
            category={category}
            subcategory={subcategory}
            collection={collection}
          />
        ) : (
          <PolicySetting
            setting={setting}
            category={category}
            subcategory={subcategory}
            collection={collection}
          />
        )}
      </div>
    ) : (
      <div key={setting.name}>
        <DeviceConfigParam configParam={setting} />
      </div>
    );
  };

  return (
    <article className="my-settings-subcategory mt-1">
      <h6 className="subcategory-title">{subcategory}</h6>
      {Object.entries(settings).map(([collection, collectionSettings]) => (
        <React.Fragment key={collection}>
          {collection !== MAIN ? (
            <h6 className="collection-title">{collection}</h6>
          ) : null}
          {Object.entries(collectionSettings).map(([group, settings]) => (
            <React.Fragment key={group}>
              {group !== DEFAULT_GROUP ? (
                <h6 className="group-title">{group}</h6>
              ) : null}
              {settings.map(setting =>
                getSettingComponent(setting, collection)
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </article>
  );
};

const Category: React.FC<{
  name: string;
  settings: CategorySettings<Setting | ConfigParam>;
  expanded: boolean;
}> = ({ name, settings, expanded }) => (
  <ExpandableCard
    collapsedContent={<div className="category-title pl-5">{name}</div>}
    dropdown
    expanded={expanded}
  >
    <section className="my-settings-category">
      {Object.entries(settings).map(([subcategory, subcategorySettings]) => (
        <Subcategory
          key={subcategory}
          category={name}
          subcategory={subcategory}
          settings={subcategorySettings}
        />
      ))}
    </section>
  </ExpandableCard>
);

const SoftwareUpdateCategory: React.FC<{
  id?: string;
  model: string;
  settings: Record<string, SectionSetting>;
  dirty: boolean;
  expanded: boolean;
}> = ({ id, model, settings, expanded, dirty }) => {
  const { t } = useTranslation();
  const [view] = useQueryParamString<"view">("view");

  const changesView = view === "changes";

  return (
    <ExpandableCard
      collapsedContent={
        <div className="category-title pl-5">{t("SOFTWARE_UPDATE")}</div>
      }
      dropdown
      expanded={expanded}
    >
      <section className="my-settings-category">
        {!changesView && (
          <UpdateNowButton
            id={id}
            model={model}
            dirty={dirty}
            className="text-right"
          />
        )}
        <ProvisionedUpdateSoftwareForm
          model={model}
          settings={settings}
          revertAction={
            changesView ? RevertAction.DISCARD : RevertAction.REVERT
          }
        />
      </section>
    </ExpandableCard>
  );
};

export const SettingsMapper: React.FC<SettingsMapperProps> = ({
  device,
  view,
  deviceModel,
  dirty,
  inheritedBy,
  settingsToDisplay,
  swUpdateSettingsToDisplay,
  loading
}) => {
  const { t } = useTranslation();
  const categories = Object.entries(settingsToDisplay);
  const hasSWUpdateSettings = Object.keys(swUpdateSettingsToDisplay).length > 0;
  const hasSettings = categories.length > 0 || hasSWUpdateSettings;
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const showInheritance = policyScope === PolicyDeploymentScope.Device;
  let sectionInfo = "";
  switch (view) {
    case MY_SETTINGS_POLICY_VIEW:
      sectionInfo = t("MY_SETTINGS_POLICY_INFO", { devices: inheritedBy });
      break;
    case MY_SETTINGS_DEVICE_CONFIG_VIEW:
      sectionInfo = t("MY_SETTINGS_DEVICE_CONFIG_INFO", {
        device: handleNullFields(device?.name)
      });
      break;
    case MY_SETTINGS_CHANGES_VIEW:
      sectionInfo = t("UNSAVED_CHANGES_INFO");
      break;
  }
  return (
    <Loader loading={loading}>
      {hasSettings && (
        <div className="my-settings-info mb-15">{sectionInfo}</div>
      )}
      {hasSettings ? (
        <div className="form grid scrollable" data-testid="my-settings-view">
          <div className="mb-3">
            <div className="headers mb-1 grid">
              <span className="col-7 pl-5">{t("CATEGORY")}</span>
              {view === MY_SETTINGS_DEVICE_CONFIG_VIEW ? (
                <span className="col-3">
                  {t("SOURCE")}
                  <ToolTip
                    iconTitle="info"
                    iconReplacement={
                      <PolyIcon icon="info_circle" className="ml-5" />
                    }
                    position="bottom"
                    size="lg"
                  >
                    <span>
                      <Trans i18nKey="MY_SETTINGS_SOURCE_INFO">
                        Description: More details can be found in
                        <strong>Lens Help</strong>
                      </Trans>
                    </span>
                  </ToolTip>
                </span>
              ) : showInheritance ? (
                <span className="col-3">
                  {t("POLICY_INHERITANCE")}
                  <ToolTip
                    iconTitle="info"
                    iconReplacement={
                      <PolyIcon icon="info_circle" className="ml-5" />
                    }
                    position="bottom"
                    size="lg"
                  >
                    <span>{t("MY_SETTINGS_INHERITANCE_INFO")}</span>
                  </ToolTip>
                </span>
              ) : null}

              {view === MY_SETTINGS_POLICY_VIEW ? (
                <span className="col-2">
                  <ProtectedLink
                    to={infiniteNestLink("/remove")}
                    className="remove-all"
                    requirement="update:device"
                  >
                    {t("REMOVE_ALL")}
                  </ProtectedLink>
                </span>
              ) : null}
            </div>
            {
              <>
                {categories.map(([category, categorySettings], index) => (
                  <Category
                    name={category}
                    settings={categorySettings}
                    key={`${view}-${category}`}
                    expanded={index === 0}
                  />
                ))}
                {hasSWUpdateSettings && (
                  <SoftwareUpdateCategory
                    id={device?.id}
                    model={deviceModel}
                    settings={swUpdateSettingsToDisplay}
                    expanded={categories.length === 0}
                    dirty={dirty}
                    key={`${view}-swupdate`}
                  />
                )}
              </>
            }
          </div>
        </div>
      ) : (
        <div data-testid="no-results">
          {view === MY_SETTINGS_CHANGES_VIEW ? (
            <div className="no-results-indicator">
              <img src="/img/astrounaut.png" alt="" />
              <p className="text-md">{t("NO_CHANGED_SETTINGS")}</p>
            </div>
          ) : (
            <Animation
              animation={{
                light: lightAnimation,
                dark: darkAnimation
              }}
              title={
                view === MY_SETTINGS_POLICY_VIEW
                  ? t("NO_POLICY_SETTINGS")
                  : t("NO_DEVICE_CONFIG")
              }
            />
          )}
        </div>
      )}
    </Loader>
  );
};
